import {
  Box,
  Heading,
  Image,
  VStack,
  HStack,
  Avatar,
  Flex,
  Stack,
  IconButton,
  Separator,
} from '@chakra-ui/react'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { useHover } from '@uidotdev/usehooks'

import { useState } from 'react'
import { IoVolumeHighSharp, IoVolumeMute } from 'react-icons/io5'
import ReactPlayer from 'react-player'

type VideoCardProps = {
  video?: string
  summary?: string
  url?: string
  title?: string
  tag?: string
  subject?: string
  firstName?: string
  lastName?: string
  avatar?: string
  image?: string
  companyLogo?: string
}

export const VideoCard = (props: VideoCardProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const [ref, hovering] = useHover()
  const [isMuted, setIsMuted] = useState(true)

  return (
    <Stack gap={5} width={'full'}>
      <Flex
        ref={ref}
        alignItems={'flex-end'}
        aspectRatio={16 / 9}
        backgroundImage={props.image}
        backgroundPosition={'center'}
        backgroundRepeat={'no-repeat'}
        backgroundSize={'cover'}
        borderRadius={'xl'}
        boxShadow={'0px 5px 20px -10px rgba(0, 0, 0, 0.8)'}
        direction={'row'}
        height={'full'}
        maxW={'full'}
        position={'relative'}
        transition={'all 0.2s ease-in'}
        width={'full'}
      >
        {props.video ? (
          <Box borderRadius={'xl'} overflow={'hidden'} width={'full'}>
            <NextLink href={props.url || '#'} prefetch={false}>
              <ReactPlayer
                controls={false}
                height={'auto'}
                light={hovering ? false : true}
                loop={true}
                muted={isMuted} // Use the state variable for muted prop
                playIcon={<></>}
                playing={hovering ? true : false}
                style={{ opacity: hovering ? 1 : 0, transition: 'opacity 0.5s ease' }}
                url={props.video}
                volume={isMuted ? 0 : 1}
                width={'100%'}
              />
            </NextLink>
          </Box>
        ) : (
          <Box borderRadius={'xl'} overflow={'hidden'} width={'full'}>
            <NextLink href={props.url || '#'} prefetch={false}>
              <Image
                src={props.image}
                transform={hovering && 'scale(1.05)'}
                transition={'all 0.2s ease-in-out'}
              />
            </NextLink>
          </Box>
        )}

        {/** BEGIN MUTE TOGGLE */}
        {props.video && (
          <Box bottom={0} margin={2} opacity={hovering ? 1 : 0} position={'absolute'} right={0}>
            <IconButton
              aria-label={'mute toggle'}
              variant={'subtle'}
              size={'md'}
              // Toggle mute state onClick
              onClick={() => setIsMuted((prevMuted) => !prevMuted)}
            >
              {isMuted ? <IoVolumeMute /> : <IoVolumeHighSharp />}
            </IconButton>
          </Box>
        )}
        {/** END MUTE TOGGLE */}
      </Flex>
      <HStack align={'flex-start'} gap={4} width={'full'}>
        <Avatar.Root size={'md'}>
          <Avatar.Fallback />
          <Avatar.Image src={props.avatar} />
        </Avatar.Root>
        <VStack align={'flex-start'} gap={0} width={'full'}>
          <NextLink href={props.url || '#'} prefetch={false}>
            <Heading size={getBreakpointValue(['lg', 'xl', 'md', 'md', 'lg'])}>{props.title}</Heading>
          </NextLink>
          <HStack align={'center'} gap={3} opacity={0.5} width={'full'}>
            <Heading size={'sm'}>
              {props.firstName} {props.lastName}
            </Heading>
            <Separator height={'1em'} orientation={'vertical'} />
            {props.companyLogo && (
              <Box h={'auto'} overflow={'hidden'} position={'relative'} w={'5em'}>
                <Image
                  filter={{ _light: 'invert(1)', _dark: 'invert(0)' }}
                  h={'100%'}
                  objectFit={'contain'}
                  src={props.companyLogo}
                  w={'100%'}
                />
              </Box>
            )}
          </HStack>
        </VStack>
      </HStack>
    </Stack>
  )
}
